import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WELCOME_MSG = '¡Hola! Soy el asistente oficial de TuLiga.online. ¿En qué te puedo ayudar hoy con tus transmisiones, marcadores o el uso de la plataforma?';

export default function SupportChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME_MSG }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll al fondo
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Focus en el input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const newUserMsg = { role: 'user', content: text };
    const newMessages = [...messages, newUserMsg];
    setMessages(newMessages);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/support/chat`, {
        message: text,
        session_id: sessionId,
        history: messages.slice(-12), // ultimos 6 turnos
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (!sessionId && res.data.session_id) setSessionId(res.data.session_id);
    } catch (e) {
      console.error('Support chat error:', e);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Lo siento, hubo un error. Contáctanos directo por WhatsApp: +52 81 4585 8193'
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Botón flotante - posicionado arriba del WhatsApp button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#D946EF] hover:scale-110 active:scale-95 shadow-2xl shadow-[#7C3AED]/50 flex items-center justify-center transition-transform group"
          data-testid="support-chat-open-btn"
          aria-label="Abrir asistente"
        >
          <MessageCircle className="w-7 h-7 text-white" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-[#0A0A0A] animate-pulse" />
          <span className="absolute right-full mr-3 hidden group-hover:block bg-black/90 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap">
            ¿Necesitas ayuda?
          </span>
        </button>
      )}

      {/* Ventana de chat */}
      {isOpen && (
        <div
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[70vh] sm:h-[560px] max-h-[640px] bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden"
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#7C3AED] to-[#D946EF] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm leading-tight">Asistente TuLiga.online</h3>
                <p className="text-white/80 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  En línea
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="support-chat-close-btn"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Mensajes */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F12]"
            data-testid="support-chat-messages"
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-[#7C3AED] to-[#D946EF] text-white rounded-br-md'
                      : 'bg-white/10 text-gray-100 rounded-bl-md border border-white/5'
                  }`}
                  data-testid={`support-chat-bubble-${msg.role}`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="support-chat-typing">
                <div className="bg-white/10 border border-white/5 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    <span className="text-gray-400 text-xs ml-2">escribiendo...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-3 bg-[#0A0A0A] flex-shrink-0">
            <div className="flex items-center gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu pregunta..."
                rows={1}
                disabled={loading}
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#7C3AED]/50 resize-none max-h-24"
                data-testid="support-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#D946EF] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-opacity flex-shrink-0"
                data-testid="support-chat-send-btn"
                aria-label="Enviar"
              >
                {loading
                  ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                  : <Send className="w-5 h-5 text-white" />}
              </button>
            </div>
            <p className="text-[10px] text-gray-500 text-center mt-2">
              ¿Más ayuda? WhatsApp: <a href="https://wa.me/528145858193" target="_blank" rel="noreferrer" className="text-[#7C3AED] hover:underline">+52 81 4585 8193</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
